import { usePlan } from '../store/planStore.js';
import { householdPortions } from '../../domain/portions.js';
import { round2 } from '../../domain/units.js';
import { STORES, WEEKDAYS } from '../../domain/types.js';
import type { Person, Store, Weekday } from '../../domain/types.js';
import type { Effort } from '../store/settings.js';
import { SharingPanel } from '../components/Sharing.js';

const DAY_NAME: Record<Weekday, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const STORE_NAME: Record<Store, string> = { aldi: 'Aldi', tesco: 'Tesco', lidl: 'Lidl' };
const EFFORTS: Effort[] = ['low', 'medium', 'high'];

export function Settings({ onDone }: { onDone: () => void }): React.JSX.Element {
  const { ready, settings, updateSettings } = usePlan();
  if (!ready) return <p>Loading</p>;

  const setPerson = (id: string, change: (p: Person) => Person): void =>
    updateSettings({
      ...settings,
      people: settings.people.map((p) => (p.id === id ? change(p) : p)),
    });

  return (
    <section className="settings">
      <header className="plan-head">
        <h1>Settings</h1>
        <p className="plan-count">
          Cooking for {householdPortions(settings.people)} portions
        </p>
      </header>

      <SharingPanel />

      <h2 className="section-head">Who is eating</h2>
      {settings.people.map((person) => (
        <div className="person" key={person.id}>
          <span>{person.name}</span>
          <span className="factor">{person.portionFactor}</span>
          <button type="button" aria-label={`Decrease ${person.name}`}
                  onClick={() => setPerson(person.id, (p) => ({
                    ...p, portionFactor: round2(Math.max(0, p.portionFactor - 0.1)),
                  }))}>−</button>
          <button type="button" aria-label={`Increase ${person.name}`}
                  onClick={() => setPerson(person.id, (p) => ({
                    ...p, portionFactor: round2(p.portionFactor + 0.1),
                  }))}>+</button>
        </div>
      ))}

      <h2 className="section-head">Nursery days</h2>
      {settings.people.filter((p) => p.personType === 'child').map((child) => (
        <div className="daypick" key={child.id}>
          <span>{child.name}'s nursery days</span>
          <div className="daypick-row">
            {WEEKDAYS.map((day) => (
              <button
                key={day}
                type="button"
                aria-label={`${child.name} on ${DAY_NAME[day]}`}
                aria-pressed={child.lunchDays.includes(day)}
                onClick={() => setPerson(child.id, (p) => ({
                  ...p,
                  lunchDays: p.lunchDays.includes(day)
                    ? p.lunchDays.filter((d) => d !== day)
                    : [...p.lunchDays, day],
                }))}
              >
                {DAY_NAME[day].slice(0, 1)}
              </button>
            ))}
          </div>
        </div>
      ))}

      <h2 className="section-head">How much cooking each night can take</h2>
      {WEEKDAYS.map((day) => (
        <div className="daypick" key={day}>
          <span>{DAY_NAME[day]}</span>
          <div className="daypick-row">
            {EFFORTS.map((level) => (
              <button
                key={level}
                type="button"
                aria-label={`${DAY_NAME[day]} ${level}`}
                aria-pressed={settings.effort[day] === level}
                onClick={() => updateSettings({
                  ...settings, effort: { ...settings.effort, [day]: level },
                })}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      ))}

      <h2 className="section-head">Usual shop</h2>
      <div className="daypick-row">
        {STORES.map((store) => (
          <button
            key={store}
            type="button"
            aria-label={`Default store ${STORE_NAME[store]}`}
            aria-pressed={settings.defaultStore === store}
            onClick={() => updateSettings({ ...settings, defaultStore: store })}
          >
            {STORE_NAME[store]}
          </button>
        ))}
      </div>

      <button type="button" className="primary" onClick={onDone}>Done</button>
    </section>
  );
}
